import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaCashRegister,
  FaArrowLeft,
  FaPlus,
  FaTrash,
  FaSync,
  FaCheckCircle,
  FaExclamationTriangle,
  FaInfoCircle,
  FaHistory,
  FaMoneyBillWave,
  FaCreditCard,
  FaGlobe,
  FaEllipsisH,
} from "react-icons/fa";
import {
  createPosSettlement,
  listPosSettlements,
  deletePosSettlement,
} from "./posSettlementService";
import "./PosSettlement.css";

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const today = () => new Date().toISOString().split("T")[0];

const blankForm = () => ({
  settlement_date: today(),
  shift: "Full Day",
  cashier: "",
  opening_cash: "",
  cash_sales: "",
  card_sales: "",
  online_sales: "",
  other_sales: "",
  cash_expenses: "",
  actual_closing_cash: "",
  notes: "",
});

// ── Component ─────────────────────────────────────────────────────────────────

const PosSettlementPage = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("new"); // "new" | "history"

  // ── Form state ──────────────────────────────────────────────────────────────
  const [form, setForm] = useState(blankForm());
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // ── History state ───────────────────────────────────────────────────────────
  const [history, setHistory] = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [deleting, setDeleting] = useState(null);

  // ── Computed values ─────────────────────────────────────────────────────────
  const opening = parseFloat(form.opening_cash) || 0;
  const cashSales = parseFloat(form.cash_sales) || 0;
  const cardSales = parseFloat(form.card_sales) || 0;
  const onlineSales = parseFloat(form.online_sales) || 0;
  const otherSales = parseFloat(form.other_sales) || 0;
  const cashExpenses = parseFloat(form.cash_expenses) || 0;
  const actualClosing = parseFloat(form.actual_closing_cash) || 0;

  const totalSales = cashSales + cardSales + onlineSales + otherSales;
  const expectedClosing = opening + cashSales - cashExpenses;
  const variance = actualClosing - expectedClosing;

  // ── Fetch history ───────────────────────────────────────────────────────────
  const fetchHistory = useCallback(async () => {
    setHistLoading(true);
    try {
      const data = await listPosSettlements();
      setHistory(data);
    } catch {
      // silently fail
    } finally {
      setHistLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === "history") fetchHistory();
  }, [tab, fetchHistory]);

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleReset = () => {
    setForm(blankForm());
    setSuccessMsg("");
    setErrorMsg("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMsg("");
    setErrorMsg("");

    if (!form.settlement_date) {
      setErrorMsg("Please select a settlement date.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        settlement_date: form.settlement_date,
        shift: form.shift || null,
        cashier: form.cashier || null,
        opening_cash: parseFloat(form.opening_cash) || 0,
        cash_sales: parseFloat(form.cash_sales) || 0,
        card_sales: parseFloat(form.card_sales) || 0,
        online_sales: parseFloat(form.online_sales) || 0,
        other_sales: parseFloat(form.other_sales) || 0,
        cash_expenses: parseFloat(form.cash_expenses) || 0,
        actual_closing_cash: parseFloat(form.actual_closing_cash) || 0,
        notes: form.notes || null,
      };
      const res = await createPosSettlement(payload);
      setSuccessMsg(
        `Settlement #${res.id} saved! Status: ${res.status}. Variance: AED ${fmt(res.variance)}`
      );
      setForm(blankForm());
    } catch (err) {
      const detail = err?.response?.data?.detail || "Failed to save settlement.";
      setErrorMsg(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this settlement record?")) return;
    setDeleting(id);
    try {
      await deletePosSettlement(id);
      setHistory((prev) => prev.filter((r) => r.id !== id));
    } catch {
      alert("Failed to delete.");
    } finally {
      setDeleting(null);
    }
  };

  // ── Variance display ─────────────────────────────────────────────────────────
  const varianceClass =
    variance === 0 ? "balanced" : variance > 0 ? "over" : "short";
  const varianceLabel =
    variance === 0
      ? "Balanced — No Discrepancy"
      : variance > 0
      ? "Over (Cash Surplus)"
      : "Short (Cash Deficit)";

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="pos-page">
      {/* Header */}
      <div className="pos-header">
        <div className="pos-title-group">
          <h2>
            <FaCashRegister style={{ color: "#4f46e5" }} />
            POS Settlement
          </h2>
          <p>Record end-of-shift cash reconciliation and sales summary</p>
        </div>
        <div className="pos-header-actions">
          <button
            className="pos-btn pos-btn-secondary"
            onClick={() => navigate("/dashboard")}
          >
            <FaArrowLeft /> Dashboard
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="pos-tabs">
        <button
          className={`pos-tab ${tab === "new" ? "active" : ""}`}
          onClick={() => setTab("new")}
        >
          <FaPlus style={{ marginRight: 5 }} />
          New Settlement
        </button>
        <button
          className={`pos-tab ${tab === "history" ? "active" : ""}`}
          onClick={() => setTab("history")}
        >
          <FaHistory style={{ marginRight: 5 }} />
          Settlement History
        </button>
      </div>

      {/* ── NEW SETTLEMENT FORM ─────────────────────────────────────── */}
      {tab === "new" && (
        <form onSubmit={handleSubmit}>
          {/* Messages */}
          {successMsg && (
            <div className="pos-alert success">
              <FaCheckCircle /> {successMsg}
            </div>
          )}
          {errorMsg && (
            <div className="pos-alert error">
              <FaExclamationTriangle /> {errorMsg}
            </div>
          )}

          {/* Live summary cards */}
          <div className="pos-summary-grid">
            <div className="pos-summary-card blue">
              <div className="pos-card-label">Total Sales</div>
              <div className="pos-card-value">AED {fmt(totalSales)}</div>
            </div>
            <div className="pos-summary-card green">
              <div className="pos-card-label">Expected Closing</div>
              <div className="pos-card-value green">AED {fmt(expectedClosing)}</div>
            </div>
            <div className="pos-summary-card">
              <div className="pos-card-label">Actual Closing</div>
              <div className="pos-card-value">AED {fmt(actualClosing)}</div>
            </div>
            <div
              className={`pos-summary-card ${
                variance === 0 ? "green" : variance > 0 ? "purple" : "red"
              }`}
            >
              <div className="pos-card-label">Variance</div>
              <div
                className={`pos-card-value ${
                  variance === 0 ? "green" : variance < 0 ? "red" : ""
                }`}
              >
                {variance >= 0 ? "+" : ""}AED {fmt(variance)}
              </div>
            </div>
          </div>

          {/* Section 1: Session Info */}
          <div className="pos-panel">
            <div className="pos-panel-title">
              <FaInfoCircle style={{ color: "#4f46e5" }} /> Session Information
            </div>
            <div className="pos-form-grid">
              <div className="pos-field">
                <label>Settlement Date *</label>
                <input
                  type="date"
                  name="settlement_date"
                  value={form.settlement_date}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="pos-field">
                <label>Shift</label>
                <select name="shift" value={form.shift} onChange={handleChange}>
                  <option value="Full Day">Full Day</option>
                  <option value="Morning">Morning</option>
                  <option value="Afternoon">Afternoon</option>
                  <option value="Evening">Evening</option>
                  <option value="Night">Night</option>
                </select>
              </div>
              <div className="pos-field">
                <label>Cashier Name</label>
                <input
                  type="text"
                  name="cashier"
                  value={form.cashier}
                  onChange={handleChange}
                  placeholder="e.g. Ahmed Ali"
                />
              </div>
              <div className="pos-field">
                <label>Opening Cash (AED)</label>
                <input
                  type="number"
                  name="opening_cash"
                  value={form.opening_cash}
                  onChange={handleChange}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
          </div>

          {/* Section 2: Sales Breakdown */}
          <div className="pos-panel">
            <div className="pos-panel-title">
              <FaMoneyBillWave style={{ color: "#10b981" }} /> Sales Breakdown
            </div>
            <div className="pos-form-grid">
              <div className="pos-field">
                <label>
                  <FaMoneyBillWave
                    style={{ marginRight: 5, color: "#10b981" }}
                  />
                  Cash Sales (AED)
                </label>
                <input
                  type="number"
                  name="cash_sales"
                  value={form.cash_sales}
                  onChange={handleChange}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="pos-field">
                <label>
                  <FaCreditCard style={{ marginRight: 5, color: "#3b82f6" }} />
                  Card / POS Sales (AED)
                </label>
                <input
                  type="number"
                  name="card_sales"
                  value={form.card_sales}
                  onChange={handleChange}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="pos-field">
                <label>
                  <FaGlobe style={{ marginRight: 5, color: "#8b5cf6" }} />
                  Online / Transfer Sales (AED)
                </label>
                <input
                  type="number"
                  name="online_sales"
                  value={form.online_sales}
                  onChange={handleChange}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="pos-field">
                <label>
                  <FaEllipsisH style={{ marginRight: 5, color: "#f59e0b" }} />
                  Other Sales (AED)
                </label>
                <input
                  type="number"
                  name="other_sales"
                  value={form.other_sales}
                  onChange={handleChange}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>

            {/* Total sales inline */}
            <div style={{ marginTop: 16, textAlign: "right", fontSize: "0.9rem", color: "#475569", fontWeight: 600 }}>
              Total Sales:{" "}
              <span style={{ color: "#1e293b", fontSize: "1.05rem" }}>
                AED {fmt(totalSales)}
              </span>
            </div>
          </div>

          {/* Section 3: Cash Out & Closing */}
          <div className="pos-panel">
            <div className="pos-panel-title">
              <FaMoneyBillWave style={{ color: "#ef4444" }} /> Cash Out & Closing Count
            </div>
            <div className="pos-form-grid">
              <div className="pos-field">
                <label>Cash Expenses / Petty Cash Out (AED)</label>
                <input
                  type="number"
                  name="cash_expenses"
                  value={form.cash_expenses}
                  onChange={handleChange}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="pos-field">
                <label>Actual Closing Cash — Physical Count (AED)</label>
                <input
                  type="number"
                  name="actual_closing_cash"
                  value={form.actual_closing_cash}
                  onChange={handleChange}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>

            <div className="pos-divider" />

            {/* Variance box */}
            <div className={`pos-variance-box ${varianceClass}`}>
              <div>
                <div className="pos-variance-label">{varianceLabel}</div>
                <div style={{ fontSize: "0.78rem", opacity: 0.8, marginTop: 2 }}>
                  Expected: AED {fmt(expectedClosing)} &nbsp;|&nbsp; Actual: AED{" "}
                  {fmt(actualClosing)}
                </div>
              </div>
              <div className="pos-variance-amount">
                {variance >= 0 ? "+" : ""}AED {fmt(Math.abs(variance))}
              </div>
            </div>

            <div className="pos-field" style={{ marginTop: 18 }}>
              <label>Notes / Remarks</label>
              <textarea
                name="notes"
                value={form.notes}
                onChange={handleChange}
                placeholder="Any additional notes about this settlement..."
              />
            </div>
          </div>

          {/* Submit row */}
          <div className="pos-submit-row">
            <button
              type="button"
              className="pos-btn pos-btn-secondary"
              onClick={handleReset}
            >
              <FaSync /> Reset
            </button>
            <button
              type="submit"
              className="pos-btn pos-btn-success"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <span
                    className="pos-spinner"
                    style={{ width: 14, height: 14, borderWidth: 2 }}
                  />
                  Saving...
                </>
              ) : (
                <>
                  <FaCheckCircle /> Save Settlement
                </>
              )}
            </button>
          </div>
        </form>
      )}

      {/* ── HISTORY TAB ─────────────────────────────────────────────── */}
      {tab === "history" && (
        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: 14,
            }}
          >
            <button
              className="pos-btn pos-btn-outline"
              onClick={fetchHistory}
              disabled={histLoading}
            >
              <FaSync /> Refresh
            </button>
          </div>

          {histLoading ? (
            <div className="pos-loading">
              <div className="pos-spinner" />
              Loading settlement history...
            </div>
          ) : history.length === 0 ? (
            <div className="pos-empty">
              <div className="pos-empty-icon">
                <FaCashRegister />
              </div>
              <p>No settlements recorded yet.</p>
            </div>
          ) : (
            <div className="pos-table-wrap">
              <table className="pos-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Date</th>
                    <th>Shift</th>
                    <th>Cashier</th>
                    <th className="text-right">Opening</th>
                    <th className="text-right">Total Sales</th>
                    <th className="text-right">Cash Sales</th>
                    <th className="text-right">Card Sales</th>
                    <th className="text-right">Expected</th>
                    <th className="text-right">Actual</th>
                    <th className="text-right">Variance</th>
                    <th className="text-center">Status</th>
                    <th className="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((r) => (
                    <tr key={r.id}>
                      <td>{r.id}</td>
                      <td>{r.settlement_date}</td>
                      <td>{r.shift || "-"}</td>
                      <td>{r.cashier || "-"}</td>
                      <td className="text-right">{fmt(r.opening_cash)}</td>
                      <td className="text-right">{fmt(r.total_sales)}</td>
                      <td className="text-right">{fmt(r.cash_sales)}</td>
                      <td className="text-right">{fmt(r.card_sales)}</td>
                      <td className="text-right">{fmt(r.expected_closing_cash)}</td>
                      <td className="text-right">{fmt(r.actual_closing_cash)}</td>
                      <td
                        className="text-right"
                        style={{
                          color:
                            r.variance > 0
                              ? "#1d4ed8"
                              : r.variance < 0
                              ? "#dc2626"
                              : "#059669",
                          fontWeight: 600,
                        }}
                      >
                        {r.variance >= 0 ? "+" : ""}
                        {fmt(r.variance)}
                      </td>
                      <td className="text-center">
                        <span
                          className={`pos-badge ${r.status?.toLowerCase()}`}
                        >
                          {r.status}
                        </span>
                      </td>
                      <td className="text-center">
                        <button
                          className="pos-icon-btn"
                          onClick={() => handleDelete(r.id)}
                          disabled={deleting === r.id}
                          title="Delete"
                        >
                          {deleting === r.id ? (
                            <span
                              className="pos-spinner"
                              style={{ width: 12, height: 12, borderWidth: 2 }}
                            />
                          ) : (
                            <FaTrash />
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={4}>Totals</td>
                    <td className="text-right">
                      {fmt(history.reduce((s, r) => s + (r.opening_cash || 0), 0))}
                    </td>
                    <td className="text-right">
                      {fmt(history.reduce((s, r) => s + (r.total_sales || 0), 0))}
                    </td>
                    <td className="text-right">
                      {fmt(history.reduce((s, r) => s + (r.cash_sales || 0), 0))}
                    </td>
                    <td className="text-right">
                      {fmt(history.reduce((s, r) => s + (r.card_sales || 0), 0))}
                    </td>
                    <td className="text-right">
                      {fmt(history.reduce((s, r) => s + (r.expected_closing_cash || 0), 0))}
                    </td>
                    <td className="text-right">
                      {fmt(history.reduce((s, r) => s + (r.actual_closing_cash || 0), 0))}
                    </td>
                    <td className="text-right">
                      {fmt(history.reduce((s, r) => s + (r.variance || 0), 0))}
                    </td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PosSettlementPage;
